import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Questions' },
    { id: 'solar', name: 'Solar' },
    { id: 'hvac', name: 'HVAC' },
    { id: 'insulation', name: 'Insulation' },
    { id: 'financing', name: 'Financing' },
    { id: 'rebates', name: 'Rebates' }
  ];

  const faqs = [
    {
      category: 'solar',
      question: 'How much can I save with solar panels in Florida?',
      answer:
        'Florida homeowners typically save 50-70% on their electricity bills with solar. With an average system size of 8-10kW, you can expect to save $100-150 per month, which adds up to $1,200-1,800 annually. Combined with the 30% federal tax credit and Florida incentives, most systems pay for themselves in 6-8 years.'
    },
    {
      category: 'hvac',
      question: 'What is the difference between a heat pump and an air conditioner?',
      answer:
        "A heat pump provides both heating and cooling, making it more versatile and energy‑efficient than a traditional AC unit. In Florida's mild winters, heat pumps are 2‑3 times more efficient than electric resistance heating. They work by transferring heat rather than generating it, resulting in lower energy bills year‑round."
    },
    {
      category: 'solar',
      question: 'How long does solar panel installation take?',
      answer:
        'Solar panel installation typically takes 1‑3 days depending on system size. The entire process from consultation to activation usually takes 4‑8 weeks, including permitting, utility approval, and inspection. We handle all paperwork and coordinate with local authorities to make the process seamless for you.'
    },
    {
      category: 'rebates',
      question: 'What rebates and incentives are available in Florida?',
      answer:
        'Florida homeowners can access multiple incentives: 30% Federal Solar Tax Credit (through 2032), Florida Solar Rights Act property tax exemption, utility company rebates up to $1,500 for HVAC, federal tax credits up to $2,000 for heat pumps, and up to $1,200 annually for insulation improvements under the Inflation Reduction Act. Our team helps you maximize all available incentives.'
    },
    {
      category: 'financing',
      question: 'Do you offer financing options?',
      answer:
        'Yes! We offer flexible financing with $0 down, low monthly payments, and terms up to 25 years. We work with multiple lenders to find the best rates for your situation. Many customers find their monthly loan payment is less than their energy savings, making upgrades cash‑flow positive from day one. We also offer special promotions throughout the year.'
    },
    {
      category: 'insulation',
      question: "How does attic insulation help in Florida's hot climate?",
      answer:
        'Attic insulation is crucial in Florida because attics can reach 150°F in summer. Proper R‑30 insulation creates a thermal barrier that prevents heat from radiating into your living space, reducing your AC workload by 30‑40%. This means lower energy bills, more consistent temperatures, and extended HVAC lifespan. The investment typically pays for itself in 2‑3 years.'
    },
    {
      category: 'solar',
      question: 'Will solar panels work during a power outage?',
      answer:
        'Standard grid‑tied solar systems automatically shut off during power outages for safety reasons. However, we offer battery backup systems that allow you to use solar power during outages. These systems store excess energy generated during the day and provide power when the grid is down, giving you energy independence and peace of mind.'
    },
    {
      category: 'hvac',
      question: 'How often should I replace my HVAC system?',
      answer:
        'Most HVAC systems last 10‑15 years in Florida’s climate. Signs you need replacement include: frequent repairs, rising energy bills, uneven temperatures, strange noises, or age over 12 years. Modern high‑efficiency systems can reduce energy costs by 30‑50% compared to older units. We offer free assessments to determine if repair or replacement is more cost‑effective.'
    },
    {
      category: 'solar',
      question: 'Do solar panels require maintenance?',
      answer:
        "Solar panels require minimal maintenance. Florida's frequent rain naturally cleans panels, but we recommend professional cleaning 2‑4 times per year for optimal performance. Our Premium and Elite Protection Plans include regular cleaning and inspections. Most manufacturers offer 25‑year warranties, and panels typically maintain 85‑90% efficiency after 25 years."
    },
    {
      category: 'insulation',
      question: 'What is a solar attic fan and how does it work?',
      answer:
        'A solar attic fan is a ventilation system powered entirely by solar energy. It extracts hot air from your attic, reducing temperatures by 20‑40°F. This decreases the heat load on your AC system, extends roof lifespan, and prevents moisture buildup. Since it\'s solar‑powered, it has zero operating costs and works hardest when you need it most – during hot, sunny days.'
    },
    {
      category: 'financing',
      question: 'What is the typical ROI for energy efficiency upgrades?',
      answer:
        'ROI varies by upgrade: Solar panels typically pay for themselves in 6‑8 years with 25+ year lifespan. High‑efficiency HVAC systems recover costs in 5‑7 years. Attic insulation pays back in 2‑3 years. Solar attic fans in 3‑4 years. Combined upgrades often achieve 50‑60% energy savings, with many customers seeing positive cash flow from month one when financed.'
    },
    {
      category: 'rebates',
      question: 'How do I claim the federal solar tax credit?',
      answer:
        'The federal solar tax credit (ITC) allows you to deduct 30% of your solar installation cost from your federal taxes. You claim it using IRS Form 5695 when filing your tax return. If the credit exceeds your tax liability, you can roll it over to future years. We provide all necessary documentation, and we recommend consulting with a tax professional to maximize your benefits.'
    },
    {
      category: 'hvac',
      question: 'What SEER rating should I look for in Florida?',
      answer:
        "For Florida's climate, we recommend HVAC systems with a SEER rating of 16 or higher. Higher SEER ratings mean better efficiency and lower operating costs. A SEER 18 system uses about 30% less energy than a SEER 13 system. While higher SEER units cost more upfront, the energy savings typically justify the investment within 3‑5 years, especially with available rebates."
    },
    {
      category: 'insulation',
      question: 'How much insulation do I need in my Florida attic?',
      answer:
        'Florida building codes recommend R‑30 to R‑38 insulation for attics. Most older homes have R‑11 to R‑19, which is insufficient. Upgrading to R‑30 can reduce cooling costs by 30‑40%. We perform free attic inspections to assess your current insulation levels and recommend the optimal solution for your home and budget.'
    },
    {
      category: 'solar',
      question: 'Will solar panels damage my roof?',
      answer:
        "When properly installed by certified professionals, solar panels actually protect your roof from weather and UV damage. We use industry‑leading mounting systems that don't compromise roof integrity. Before installation, we inspect your roof condition and recommend any necessary repairs. Our installations come with workmanship warranties, and we carry full insurance for your protection."
    },
    {
      category: 'financing',
      question: 'Can I get financing with less-than-perfect credit?',
      answer:
        'Yes! We work with multiple lenders who offer programs for various credit profiles. While better credit typically means lower rates, we have options for most situations. Some programs require as little as a 600 credit score. We also offer PACE financing (Property Assessed Clean Energy) which is based on home equity rather than credit score. Contact us for a confidential consultation.'
    }
  ];

  const filteredFaqs =
    activeCategory === 'all' ? faqs : faqs.filter((faq) => faq.category === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="pt-28 pb-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f6d7cd05bb267dfdb39c9547b7e275f3.jpg"
            alt="Energy efficient Florida home"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-white"></div>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 relative z-10">
          <div className="text-center mb-12">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 font-medium"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Find answers to common questions about our services, financing, and energy
              efficiency solutions
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => {
                  setActiveCategory(category.id);
                  setOpenIndex(null);
                }}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeCategory === category.id
                    ? 'bg-brand-red text-white'
                    : 'bg-white/80 backdrop-blur-sm text-gray-700 hover:bg-white'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="space-y-4">
            {filteredFaqs.map((faq, index) => (
              <div
                key={index}
                className="bg-brand-cream rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-lg"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-8 py-6 flex items-center justify-between text-left cursor-pointer hover:bg-brand-sand transition-colors"
                >
                  <span className="font-semibold text-gray-900 text-lg pr-4">{faq.question}</span>
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                    <i
                      className={`${
                        openIndex === index ? 'ri-subtract-line' : 'ri-add-line'
                      } text-brand-red text-2xl transition-transform duration-300`}
                    ></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-8 pb-6 text-gray-600 leading-relaxed">{faq.answer}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-brand-cream">
        <div className="max-w-[1400px] mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Our team is here to help. Contact us for personalized answers and a free consultation.
          </p>
          <a
            href="tel:+18005551234"
            className="inline-flex items-center gap-3 bg-brand-red text-white px-10 py-5 rounded-full text-lg font-bold hover:bg-brand-red-dark transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line"></i>
            Call Us: (800) 555-1234
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
